import { CheckCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap, type Authenticated } from '@/api/client';
import { ApiError, cuentaRestringidaDe, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/AuthProvider';
import { InputOTP, InputOTPSlot } from '@/components/ui/input-otp';
import { guardarTokenConfianza } from '@/lib/device';
import { animateCSS } from '@/lib/motion';

import { AuthLayout } from './AuthLayout';

type Challenge = components['schemas']['ChallengeRequired'];
interface Estado {
  challenge: Challenge;
  proposito: 'login' | 'register';
}

/** "Verifica tu acceso" (skill §2): 6 casillas, reenvío con temporizador y dispositivo de confianza. */
export function VerificacionPage() {
  const estado = useLocation().state as Estado | null;
  const { signIn, mostrarRestriccion } = useAuth();
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [confiar, setConfiar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [espera, setEspera] = useState(60);
  const casillas = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (espera <= 0) return;
    const id = window.setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [espera]);

  if (!estado) return <Navigate to="/login" replace />;
  const { challenge, proposito } = estado;
  const ruta =
    proposito === 'register' ? '/api/v1/auth/register/verify' : '/api/v1/auth/2fa/verify';

  const verificar = async (code: string) => {
    setEnviando(true);
    try {
      const result = await unwrap<Authenticated>(
        api.POST(ruta, {
          body: {
            challengeToken: challenge.challengeToken,
            code,
            trustDevice: proposito === 'login' && confiar,
          },
        }),
      );
      // Entrar con código revoca la confianza anterior: se guarda la nueva o se borra.
      if (proposito === 'login') guardarTokenConfianza(result.trustedDeviceToken);
      signIn(result);
      void navigate('/inicio', { replace: true });
    } catch (err) {
      // Suspendida o bloqueada: el modal se muestra en el login (el reto ya no sirve).
      const restringida = cuentaRestringidaDe(err);
      if (restringida) {
        mostrarRestriccion(restringida);
        void navigate('/login', { replace: true });
        return;
      }
      setCodigo('');
      void animateCSS(casillas.current, 'headShake');
      toast.error(mensajeDe(err));
      if (
        err instanceof ApiError &&
        ['CHALLENGE_INVALID', 'OTP_TOO_MANY_ATTEMPTS'].includes(err.code)
      ) {
        void navigate(proposito === 'register' ? '/registro' : '/login', { replace: true });
      }
    } finally {
      setEnviando(false);
    }
  };

  const reenviar = async () => {
    try {
      await unwrap(
        api.POST('/api/v1/auth/2fa/resend', { body: { challengeToken: challenge.challengeToken } }),
      );
      setEspera(60);
      toast.success('Te enviamos un código nuevo.');
    } catch (err) {
      const segundos = err instanceof ApiError ? Number(err.details.retryAfterSeconds ?? 0) : 0;
      if (segundos > 0) setEspera(segundos);
      toast.error(mensajeDe(err));
    }
  };

  return (
    <AuthLayout titulo={proposito === 'register' ? 'Confirma tu correo' : 'Verifica tu acceso'}>
      <p className="text-center text-sm text-texto-2">
        Te enviamos un código de 6 dígitos a{' '}
        <strong className="text-texto">{challenge.emailMasked}</strong>. Vence en{' '}
        {Math.round(challenge.expiresIn / 60)} minutos.
      </p>
      <div ref={casillas} className="my-6">
        <InputOTP
          maxLength={6}
          value={codigo}
          onChange={setCodigo}
          onComplete={(v: string) => void verificar(v)}
          disabled={enviando}
          autoFocus
          inputMode="numeric"
          pattern="^[0-9]*$"
          aria-label="Código de verificación"
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} />
          ))}
        </InputOTP>
      </div>
      {proposito === 'login' && (
        <label className="flex items-center gap-2 text-sm text-texto-2">
          <input
            type="checkbox"
            checked={confiar}
            onChange={(e) => setConfiar(e.target.checked)}
            className="h-4 w-4 accent-[#005DB9]"
          />
          No volver a pedir el código en este navegador
        </label>
      )}
      <button
        type="button"
        className="btn-cs btn-primario mt-5 w-full py-3"
        disabled={codigo.length !== 6 || enviando}
        onClick={() => void verificar(codigo)}
      >
        <CheckCircle2 size={18} /> {enviando ? 'Verificando…' : 'Verificar'}
      </button>
      <p className="mt-4 text-center text-sm text-texto-2">
        ¿No te llegó?{' '}
        {espera > 0 ? (
          <span className="text-texto-3 tabular">Reenviar código en {espera} s</span>
        ) : (
          <button
            type="button"
            className="font-bold text-azul-texto hover:underline"
            onClick={() => void reenviar()}
          >
            Reenviar código
          </button>
        )}
      </p>
      <p className="mt-2 text-center text-xs text-texto-3">
        Revisa también tu carpeta de spam o promociones.
      </p>
    </AuthLayout>
  );
}
